import type { ChangeEvent } from "react";
import InputField from "../../components/InputField";
import RadioButton from "../../components/RadioButton";
import { useToast } from "../../contexts/ToastContext";
import Badge from "../../components/Badge";
import { buildMedicationQuantityLabel } from "../../utils/medication";
import { ROUND_UP, roundToInt } from "../../utils/rounding";
import MedicationItemForm from "./MedicationItemForm";
import type { MedicationFormValue } from "./MedicationItemForm";

const PROCESS_ACTION_OPTIONS = [
    { value: "adjust", text: "ปรับจำนวน" },
    { value: "change", text: "เปลี่ยนยา" },
    { value: "cancel", text: "ยกเลิกรายการยา" },
];

interface Medication {
    id: number;
    name: string;
    quantity: number;
    unit?: string | null;
    unit_display?: string | null;
    note?: string | null;
    price?: number | null;
    stock_quantity?: number | null;
    stock_status?: string | null;
}

interface ProcessMedicationChangeRequestCardProps {
    medication: Medication;
    formValue: MedicationFormValue;
    onChange: (value: MedicationFormValue, options?: { triggerSave?: boolean }) => void;
}

/**
 * ProcessMedicationChangeRequestCard
 * 
 * A card component for processing a single medication change request item.
 *
 * @param {object} props
 * @param {object} props.medication
 *   The medication item data object.
 * @param {object} props.formValue
 *   The current form values for this medication item.
 * @param {function} props.onChange
 *   Function to call when form values change for this medication item.
 */

export default function ProcessMedicationChangeRequestCard({
    medication,
    formValue,
    onChange,
}: ProcessMedicationChangeRequestCardProps) {
    const { showErrorToast } = useToast();
    const medicationUnit = medication.unit_display ?? medication.unit ?? "";
    const resetActionFields = {
        medication: null,
        medication_id: null,
        medicine_id: null,
        name: "",
        quantity: "",
        unit: "",
        usage_instruction: "",
        usage_instructions: "",
        price: "",
        units: [],
    };

    function buildItemHeader() {
        const orderQuantityLabel = buildMedicationQuantityLabel(
            medication.quantity,
            medicationUnit
        );

        return (
            <div className="flex justify-between items-center w-full">
                <div className="flex items-center">
                    <span className="font-ui font-header text-body-base text-main-typography-system-default">{medication.name}</span>
                </div>
                <div className="flex items-center gap-3">
                    <span className="font-ui font-body text-body-base text-main-typography-system-description w-[176px]">จำนวนที่สั่ง {orderQuantityLabel}</span>
                    <span className="font-ui font-body text-body-base text-main-typography-system-description">ปัญหา</span>
                    <Badge text={medication.note || "-"} color="vivid" size="md" />
                </div>
            </div>
        );
    }

    function buildActionInputs() {
        return (
            <div className="flex items-center py-2 gap-5">
                <div className="font-ui font-header text-body-base text-main-typography-system-default">การดำเนินการ</div>
                <div className="flex gap-3">
                    {PROCESS_ACTION_OPTIONS.map((option) => (
                        <RadioButton
                            key={option.value}
                            id={`process_action_${medication.id}_${option.value}`}
                            label={option.text}
                            name={`process_action_${medication.id}`}
                            value={option.value}
                            enabled
                            showBackground={false}
                            checked={formValue.action === option.value}
                            onChange={(e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
                                const newAction = e.target.value;
                                if (formValue.action != newAction) {
                                    onChange({
                                        ...formValue,
                                        action: newAction,
                                        ...resetActionFields,
                                    });
                                    return;
                                }
                                onChange({ ...formValue, action: newAction });
                            }}
                        />
                    ))}
                </div>
            </div>
        );
    }

    function buildAdjustForm() {
        const unitPrice = Number(medication.price);
        const quantityNumber = Number(formValue.quantity);
        const totalPrice =
            Number.isFinite(unitPrice) && Number.isFinite(quantityNumber) && quantityNumber > 0
                ? roundToInt(unitPrice * quantityNumber, ROUND_UP).toString()
                : "";

        return (
            <>
                <div className="flex flex-row items-start gap-5">
                    <div className="w-[120px] flex flex-col gap-3">
                        <span className="font-ui font-header text-body-base text-main-typography-system-default">จำนวนเดิม</span>
                        <span className="font-ui font-body text-body-base text-main-typography-system-default h-8 flex items-center">{medication.quantity}</span>
                    </div>
                    <div className="w-[110px] flex flex-col gap-3">
                        <span className="font-ui font-header text-body-base text-main-typography-system-default">หน่วย</span>
                        <span className="font-ui font-body text-body-base text-main-typography-system-default h-8 flex items-center">{medicationUnit}</span>
                    </div>
                    <div className="pt-7">
                        <span className="font-ui font-body text-body-base text-main-typography-system-default w-7 h-8 flex items-center">→</span>
                    </div>
                    <div className="w-[120px]">
                        <InputField
                            label="จำนวนใหม่"
                            name={`quantity_${medication.id}`}
                            type="number"
                            placeholder="ระบุจำนวน..."
                            value={formValue.quantity || ""}
                            inputMode="numeric"
                            onChange={(e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
                                const value = e.target.value;
                                const intValue = value.replace(/[^0-9]/g, '');

                                if (intValue === "0") {
                                    showErrorToast("จำนวนยาต้องมากกว่า 0");
                                    return;
                                }

                                if (Number.isFinite(medication.stock_quantity) && Number(intValue) > medication.stock_quantity!) {
                                    showErrorToast("ไม่สามารถสั่งเกินจำนวนคงเหลือได้");
                                    return;
                                }

                                onChange({ ...formValue, quantity: intValue });
                            }}
                            errorText={medication.stock_status === "out_of_stock" || medication.stock_status === "low_stock" ? `เหลือ ${medication.stock_quantity}` : ""}
                        />
                    </div>
                    <div className="w-[110px]">
                        <InputField
                            label="หน่วย"
                            type="select"
                            name={`unit_${medication.id}`}
                            value={formValue.unit || medicationUnit}
                            choices={[[medicationUnit, medicationUnit]]}
                            onChange={(e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => onChange({ ...formValue, unit: e.target.value })}
                            disabled
                        />
                    </div>
                    <div className="w-[100px]">
                        <InputField
                            label="ราคารวม"
                            name={`total_price_${medication.id}`}
                            type="number"
                            value={totalPrice}
                            disabled
                            placeholder="-"
                        />
                    </div>
                </div>
                <div className="pt-5">
                    {buildDetailsInput()}
                </div>
            </>
        );
    }

    function buildMedicationItem() {
        return (
            <>
                <MedicationItemForm formValue={formValue} onChange={onChange} />
                <div className="pt-5">
                    {buildDetailsInput()}
                </div>
            </>
        );
    }

    function buildDetailsInput() {
        return (
            <InputField
                name={`details_${medication.id}`}
                label="หมายเหตุ"
                type="textarea"
                textareaRows={3}
                value={formValue.note || ""}
                onChange={(e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => onChange({ ...formValue, note: e.target.value })}
                placeholder="คำแนะนำหรือหมายเหตุสำหรับพนักงาน..."
                className="w-full"
            />
        );
    }

    return (
        <div className="flex flex-col rounded-lg p-5 gap-5 bg-main-background-system-default border border-main-border-system-default">
            {buildItemHeader()}
            <hr className="border-t border-main-border-system-default w-full" />
            {buildActionInputs()}
            {formValue.action === "adjust" && buildAdjustForm()}
            {formValue.action === "change" && buildMedicationItem()}
            {formValue.action === "cancel" && buildDetailsInput()}
        </div >
    );
}
